import { API_BASE } from '../config';
import type { DataTypeSpec, PropertySpec } from '../hooks/useDataTypes';

// Internal cache structure
interface CacheEntry<T> {
    data: T | null;
    promise: Promise<T> | null;
    error: Error | null;
    timestamp: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

class SchemaServiceImpl {
    private cache: Record<string, CacheEntry<any>> = {};

    private async fetchWithCache<T>(key: string, url: string): Promise<T> {
        const now = Date.now();
        const entry = this.cache[key];

        // Return cached data if valid
        if (entry?.data && (now - entry.timestamp < CACHE_TTL)) {
            return entry.data;
        }

        // Return existing promise if in flight
        if (entry?.promise) {
            return entry.promise;
        }

        // Fetch new data
        const promise = fetch(url)
            .then(async res => {
                if (!res.ok) throw new Error(`GET ${url} → ${res.status}`);
                const json = await res.json();

                // Update cache with result
                this.cache[key] = {
                    data: json[key], // e.g. json['data_types']
                    promise: null,
                    error: null,
                    timestamp: Date.now()
                };
                return json[key];
            })
            .catch(err => {
                // Update cache with error
                this.cache[key] = {
                    data: null,
                    promise: null,
                    error: err,
                    timestamp: Date.now()
                };
                throw err;
            });

        // Set promise in cache
        this.cache[key] = {
            data: null,
            promise,
            error: null,
            timestamp: now
        };

        return promise;
    }

    public async getDataTypes(): Promise<DataTypeSpec[]> {
        return this.fetchWithCache<DataTypeSpec[]>('data_types', `${API_BASE}/data_types`);
    }

    public async getFormatterTypes(): Promise<DataTypeSpec[]> {
        return this.fetchWithCache<DataTypeSpec[]>('formatter_types', `${API_BASE}/formatter_types`);
    }

    public async getPropertySpec(nodeType: 'Single' | 'Formatter', typeId: string, paramKey: string): Promise<PropertySpec | undefined> {
        try {
            let types: DataTypeSpec[] = [];

            if (nodeType === 'Single') {
                types = await this.getDataTypes();
            } else if (nodeType === 'Formatter') {
                types = await this.getFormatterTypes();
            }

            const typeSpec = types.find(t => t.id === typeId);
            return typeSpec?.properties.find(p => p.key === paramKey);
        } catch (err) {
            console.error(`SchemaService: Failed to resolve spec for ${nodeType}/${typeId}/${paramKey}`, err);
            return undefined;
        }
    }
}

export const SchemaService = new SchemaServiceImpl();
